import React, {useMemo} from 'react';
import {FormControl, Grid, TextField} from '@mui/material';
import {KeyVaultSelect} from '../../components/key-vault-select/KeyVaultSelect';
import {inputChangeEvent, mapSetState, SetState} from '../../util/FormHandlers';
import {replaceElement} from "../../util/ArrayUtil";
import {BulkAddKeysForm, KeyRange} from "../../api/Model";

function RangeInputPair({range, onChange}: {
    range: KeyRange;
    onChange: (range: KeyRange) => void;
}) {
    const handlers = useMemo(
        () => ({
            handleStartChange: inputChangeEvent(value => {
                onChange({...range, start: Number(value)});
            }),
            handleEndChange: inputChangeEvent(value => {
                onChange({...range, end: Number(value)});
            }),
        }),
        [onChange, range],
    );

    return (
        <Grid container spacing={2} alignItems="center">
            <Grid size={6}>
                <TextField
                    label="Keys From"
                    fullWidth
                    variant='standard'
                    value={range.start || ''}
                    onChange={handlers.handleStartChange}
                />
            </Grid>
            <Grid size={6}>
                <TextField
                    label="To"
                    fullWidth
                    variant='standard'
                    value={range.end || ''}
                    onChange={handlers.handleEndChange}
                />
            </Grid>
        </Grid>
    );
}

export function EditBulkKeysForm({form, setForm}: {
    form: BulkAddKeysForm;
    setForm: SetState<BulkAddKeysForm>;
}) {
    const handlers = useMemo(() => {
        const setRecord = mapSetState(setForm, 'record');
        return {
            setKeyVault: mapSetState(setRecord, 'vault'),
            handleUpdateRange: (newRange: KeyRange) => setForm(form => {
                let newRanges = form?.keyRanges || [];
                if (!newRange.start && !newRange.end) {
                    newRanges = newRanges.filter(keyRange => keyRange.id === newRange.id || keyRange.start || keyRange.end)
                }
                if (newRange.start || newRange.end) {
                    if (newRanges.every(keyRange => keyRange.id === newRange.id || keyRange.start || keyRange.end)) {
                        newRanges = [...newRanges, {id: newRanges.reduce((prevMax, next) => Math.max(prevMax, next.id), 0) + 1}];
                    }
                }
                return (form && {
                    ...form,
                    keyRanges: replaceElement(newRanges, keyRange => keyRange.id === newRange.id, newRange)
                });
            }),
        };
    }, [setForm]);

    return (
        <Grid container spacing={2} sx={{mt: 2}} direction="column">
            <Grid>
                <FormControl fullWidth>
                    <KeyVaultSelect
                        id="vault"
                        label="Vault"
                        variant="standard"
                        keyVault={form.record.vault}
                        setKeyVault={handlers.setKeyVault}
                    />
                </FormControl>
            </Grid>

            {form.keyRanges.map(range => (
                <RangeInputPair
                    key={range.id}
                    range={range}
                    onChange={handlers.handleUpdateRange}
                />
            ))}
        </Grid>
    );
}